import { DOCUMENT_TYPES } from 'app/consts/agencyDocumentTypes';
import { FileInfo } from 'app/ui/components/multi-file-uploader/MultiFileUploader';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppNS } from '../';
import {
  apiAddAgencyDocument,
  apiDeleteAgencyDocument,
  apiGetAgencyDocuments,
  apiUpdateAgencyDocument,
} from '../api/agency-documents/agency-documents';
import { AppThunk } from './index';

export interface AgencyDocumentsState {
  errorMessage: string;
  isDocumentLoading: AppNS.DocumentType | '';
  isCollectionLoading: boolean;
  isCollectionLoaded: boolean;
  isObjectDeleted: boolean;
  isDocumentDeleted: boolean;
  agencyDocuments: Array<AppNS.AgencyDocument>;
  validationErrors: Array<string>;
  isValid: boolean;
  isCheckedValid: boolean;
}

const initialState: AgencyDocumentsState = {
  errorMessage: '',
  isCollectionLoading: false,
  isDocumentLoading: '',
  isCollectionLoaded: false,
  isObjectDeleted: false,
  isDocumentDeleted: false,
  agencyDocuments: [],
  validationErrors: [],
  isValid: false,
  isCheckedValid: false,
};

function checkStateValid(state: AgencyDocumentsState) {
  const validationErrors = [];

  if (state.agencyDocuments.length < DOCUMENT_TYPES.length) {
    validationErrors.push('Please add all required documents');
  }
  state.validationErrors = validationErrors;
  state.isValid = validationErrors.length ? false : true;
}

const agencyDocuments = createSlice({
  name: 'agencyDocuments',
  initialState,
  reducers: {
    addActionStart(state: AgencyDocumentsState, action: PayloadAction<AppNS.DocumentType>) {
      state.isDocumentLoading = action.payload;
      state.isDocumentDeleted = false;
    },
    addActionSuccess(state: AgencyDocumentsState, action: PayloadAction<{ file: FileInfo; type: AppNS.DocumentType }>) {
      state.isDocumentLoading = '';
      state.isDocumentDeleted = true;
      state.errorMessage = '';
      state.agencyDocuments = [
        ...state.agencyDocuments,
        {
          id: action.payload.file.id as string,
          document: action.payload.file,
          type: action.payload.type,
        },
      ];
      checkStateValid(state);
    },
    updateActionSuccess(
      state: AgencyDocumentsState,
      action: PayloadAction<{ file: FileInfo; type: AppNS.DocumentType }>
    ) {
      state.isDocumentLoading = '';
      state.isDocumentDeleted = true;
      state.errorMessage = '';
      state.agencyDocuments = state.agencyDocuments.map((item: AppNS.AgencyDocument) => {
        if (item.type === action.payload.type) {
          return {
            id: action.payload.file.id as string,
            document: action.payload.file,
            type: action.payload.type,
          };
        }
        return item;
      });
      checkStateValid(state);
    },
    addActionFailed(state: AgencyDocumentsState, action: PayloadAction<string>) {
      state.isDocumentLoading = '';
      state.isDocumentDeleted = false;
      state.errorMessage = action.payload;
    },
    deleteActionStart(state: AgencyDocumentsState) {
      state.isCollectionLoading = true;
      state.isObjectDeleted = false;
    },
    deleteActionSuccess(state: AgencyDocumentsState) {
      state.isCollectionLoading = false;
      state.isObjectDeleted = true;
      state.errorMessage = '';
      state.agencyDocuments = [];
    },
    deleteActionFailed(state: AgencyDocumentsState, action: PayloadAction<string>) {
      state.isCollectionLoading = true;
      state.isObjectDeleted = false;
      state.errorMessage = action.payload;
    },
    getActionStart(state: AgencyDocumentsState) {
      state.isCollectionLoading = true;
      state.isCollectionLoaded = false;
    },
    getActionSuccess(state: AgencyDocumentsState, action: PayloadAction<Array<AppNS.AgencyDocument>>) {
      state.isCollectionLoading = false;
      state.isCollectionLoaded = true;
      state.errorMessage = '';
      state.agencyDocuments = action.payload;
      checkStateValid(state);
    },
    getActionFailed(state: AgencyDocumentsState, action: PayloadAction<string>) {
      state.isCollectionLoading = true;
      state.isCollectionLoaded = false;
      state.errorMessage = action.payload;
    },
    setIsCheckedValid(state: AgencyDocumentsState) {
      state.isCheckedValid = true;
    },
  },
});

export const {
  addActionFailed,
  addActionStart,
  addActionSuccess,
  deleteActionFailed,
  deleteActionStart,
  deleteActionSuccess,
  getActionFailed,
  getActionStart,
  getActionSuccess,
  setIsCheckedValid,
  updateActionSuccess,
} = agencyDocuments.actions;

export default agencyDocuments.reducer;

export const getDocuments = (): AppThunk => async (dispatch) => {
  try {
    dispatch(getActionStart());
    const response = await apiGetAgencyDocuments({
      types: DOCUMENT_TYPES.map((item) => item.id),
    });

    dispatch(getActionSuccess(response));
  } catch (err: any) {
    dispatch(getActionFailed(err.toString()));
  }
};

export const addDocument =
  (url: string, type: AppNS.DocumentType): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(addActionStart(type));
      // add or update depends on type

      const response = await apiAddAgencyDocument({
        properties: {
          type: type,
          file: url,
        },
      });
      dispatch(addActionSuccess({ file: response, type }));
    } catch (err: any) {
      dispatch(addActionFailed(err.toString()));
    }
  };

export const updateDocument =
  (url: string, type: AppNS.DocumentType, id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(addActionStart(type));
      const response = await apiUpdateAgencyDocument({
        id,
        properties: {
          type: type,
          file: url,
        },
      });
      dispatch(updateActionSuccess({ file: response, type }));
    } catch (err: any) {
      dispatch(addActionFailed(err.toString()));
    }
  };

export const deleteAgencyDocument =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(deleteActionStart());
      await apiDeleteAgencyDocument(id);
      dispatch(deleteActionSuccess());
    } catch (err: any) {
      dispatch(deleteActionFailed(err.toString()));
    }
  };
